// src/PublicPropertyView.jsx
// Vista pública de propiedad — accesible con solo propertyId, sin token
// Muestra: header simplificado, galería de fotos, datos físicos y formulario de registro
// NO muestra: plano, vídeo, ni datos financieros/legales/documentos (requieren registro)

import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Building2, HelpCircle, ChevronLeft, ChevronRight } from 'lucide-react';

import { usePublicProperty } from './hooks/usePublicProperty';
import { sectionsConfig, renderSection } from './components/PropertyView/PropertyDetailsCard';
import { LeadCaptureCard } from './components/PropertyView/LeadCaptureCard';
import LanguageToggle from './components/LanguageToggle';
import { usePropertyTranslations } from './hooks/usePropertyTranslations';

// ─────────────────────────────────────────────
// Galería de fotos — solo fotos, sin plano ni vídeo
// ─────────────────────────────────────────────
const PublicPhotoGallery = ({ property }) => {
  const { t } = useTranslation();
  const [index, setIndex] = useState(0);
  const photos = property?.photoUrls || [];

  if (photos.length === 0) return null;

  const prev = () => setIndex((i) => (i - 1 + photos.length) % photos.length);
  const next = () => setIndex((i) => (i + 1) % photos.length);

  return (
    <div className="bg-slate-800/50 backdrop-blur-xl rounded-2xl overflow-hidden border border-slate-700/50">
      <div className="aspect-video bg-slate-900 relative">
        <img
          src={photos[index]}
          alt={t('media.photoAlt', { index: index + 1 })}
          className="w-full h-full object-contain"
          onError={(e) => {
            e.target.src =
              'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="400" height="300"%3E%3Crect fill="%23334155" width="400" height="300"/%3E%3Ctext fill="%23fff" x="50%25" y="50%25" dominant-baseline="middle" text-anchor="middle"%3E' +
              encodeURIComponent(t('media.imageUnavailable')) +
              '%3C/text%3E%3C/svg%3E';
          }}
        />
        {photos.length > 1 && (
          <>
            <button
              onClick={prev}
              aria-label={t('media.prevPhoto')}
              className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white p-2 rounded-full transition-all"
            >
              <ChevronLeft className="w-6 h-6" />
            </button>
            <button
              onClick={next}
              aria-label={t('media.nextPhoto')}
              className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white p-2 rounded-full transition-all"
            >
              <ChevronRight className="w-6 h-6" />
            </button>
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-black/70 text-white px-3 py-1 rounded-full text-sm">
              {index + 1} / {photos.length}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────
// Datos físicos — solo sección "Vivienda" (physical)
// ─────────────────────────────────────────────
const PublicPropertyDetails = ({ property, translations }) => {
  const { t } = useTranslation();
  const metadata = property.propertyMetadata || {};

  return (
    <div className="bg-slate-800/50 backdrop-blur-xl rounded-2xl p-4 md:p-6 border border-slate-700/50">
      <h2 className="text-lg md:text-xl font-bold text-white mb-6">
        {t('details.title')}
      </h2>

      {/* Sección física: habitaciones, superficie, orientación... */}
      {renderSection('physical', sectionsConfig.physical, metadata, t, translations)}

    </div>
  );
};

// ─────────────────────────────────────────────
// Vista principal
// ─────────────────────────────────────────────
const PublicPropertyView = () => {
  const { propertyId } = useParams();
  const { property, loading, error } = usePublicProperty(propertyId);
  const { t } = useTranslation();
  const { translations } = usePropertyTranslations(propertyId, property?.translationsEnabled);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-amber-500 mb-4"></div>
          <div className="text-white text-xl">{t('loading.title')}</div>
          <div className="text-slate-400 text-sm mt-2">{t('loading.subtitle')}</div>
        </div>
      </div>
    );
  }

  if (error || !property) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
        <div className="bg-slate-800/50 backdrop-blur-xl rounded-2xl p-8 max-w-md w-full border border-slate-700/50 text-center">
          <div className="bg-red-500/10 p-4 rounded-xl inline-block mb-4">
            <Building2 className="w-12 h-12 text-red-400" />
          </div>
          <h2 className="text-2xl font-bold text-white mb-2">{t('error.propertyNotFound')}</h2>
          <p className="text-slate-400">{t('error.propertyUnavailable')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      <div className="max-w-7xl mx-auto p-4 md:p-8">

        {/* Header: título y descripción, sin barra de estado */}
        <div className="mb-6">
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-center gap-3 flex-1">
              <div className="bg-gradient-to-br from-amber-400 to-amber-600 p-3 rounded-xl flex-shrink-0">
                <Building2 className="w-6 h-6 text-slate-900" />
              </div>
              <div className="flex-1 min-w-0">
                <h1 className="text-2xl md:text-4xl font-bold text-white mb-1">
                  {translations?.title ?? property.title}
                </h1>
                <p className="text-sm md:text-base text-slate-400">
                  {translations?.description ?? property.description}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2 md:gap-3 flex-shrink-0">
              <span className="text-xs md:text-sm text-slate-400">{t('header.welcome')}</span>
              {property.translationsEnabled && <LanguageToggle />}
              <a
                href="/faqs"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 px-3 md:px-4 py-2 md:py-2.5 bg-slate-800/50 hover:bg-slate-700/50 border border-slate-700/50 hover:border-slate-600/50 rounded-lg transition-all group"
                title={t('header.helpTooltip')}
              >
                <HelpCircle className="w-4 h-4 md:w-5 md:h-5 text-slate-400 group-hover:text-blue-400 transition-colors" />
                <span className="hidden sm:inline text-sm md:text-base text-slate-300 group-hover:text-white transition-colors">
                  {t('header.helpButton')}
                </span>
              </a>
            </div>
          </div>
        </div>

        {/* Grid: Fotos (2/3) + Formulario de registro (1/3) */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-6 mb-6">
          <div className="lg:col-span-2">
            <PublicPhotoGallery property={property} />
          </div>
          <div>
            <LeadCaptureCard property={property} />
          </div>
        </div>

        {/* Solo datos físicos de la propiedad */}
        <PublicPropertyDetails property={property} translations={translations} />

      </div>
    </div>
  );
};

export default PublicPropertyView;
